import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Dropdown, IconButton, LinearProgress, Chip } from "@evergis/ui";
import { AggregationFunction, ClassificationType, ClassifyAttributeType } from "@evergis/api";
import { useTranslation } from "react-i18next";

import {
  SpecialFilterActions,
  SpecialFilterContainer,
  SpecialFilterHeader,
  SpecialFilterHeaderInfo,
  SpecialFilterHeaderInput,
  SpecialFilterHeaderWorkarea,
  SpecialFilterOverlay,
} from "./styled";

import { CheckboxStatisticsChart } from "../StatisticsCharts/CheckboxStatisticsChart";
import { FilledValues } from "./components/FilledValues";
import { UniqueValues } from "./components/UniqueValues";

import { useTableSpecialFilter } from "../../hooks/table/useTableSpecialFilter";
import { useStatisticsValues } from "../StatisticsCharts/hooks/useStatisticsValues";
import { useSpecialTableFilterCondition } from "./hooks/useSpecialTableFilterCondition";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { useTableFilters } from "../../hooks/table/useTableFilters";
import { getBottomHeight } from "../../ducks/layout/selectors";

import { getStatistic } from "../../resources/statistics";
import { getClassify } from "../../resources/classify";
import { getAppConfig } from "../../ducks/appConfig";

// eslint-disable-next-line max-lines-per-function
export const StringCheckboxFilter: FC = () => {
  const { t } = useTranslation("table");
  const appConfig = useSelector(getAppConfig);
  const bottomHeight = useSelector(getBottomHeight);
  const [{ layerName, attribute, filterType }, setSpecialFilter] = useTableSpecialFilter();
  const ref = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(ref);
  const chartWidth = useMemo(() => (containerWidth !== undefined ? containerWidth - 32 : undefined), [containerWidth]);
  const [selected, setSelected] = useState<string[]>([]);
  const [tableFilters, { updateFiltersByLayer }] = useTableFilters();
  const condition = useSpecialTableFilterCondition();

  const {
    data: statisicsData,
    loading: statisticsLoading,
    refetch: refetchStatistics,
  } = getStatistic.use({
    input: {
      name: layerName,
      attribute,
      condition,
      types: [AggregationFunction.Count, AggregationFunction.TotalCount, AggregationFunction.DistinctCount],
    },
    immediate: false,
  });

  const {
    data: classifyData,
    loading: classifyLoading,
    refetch: refetchClassify,
  } = getClassify.use({
    input: {
      name: layerName,
      classes: appConfig?.settings?.expressAnalytics?.uniqueValuesCount ?? 10,
      attributeType: ClassifyAttributeType.Text,
      type: ClassificationType.Unique,
      attribute,
      condition,
    },
    immediate: false,
  });

  const { count: statCount, totalCount, distinctCount } = useStatisticsValues(statisicsData);

  const data = useMemo(() => {
    if (!classifyData) {
      return [];
    }

    return Array.from({ length: classifyData.classifyResult.length }, (_, index) => ({
      groupName: classifyData.classifyResult[index].value,
      0: {
        value: classifyData.classifyResult[index].count,
      },
    }));
  }, [classifyData]);

  const options = useMemo(() => {
    if (!classifyData) {
      return [];
    }

    return Array.from({ length: classifyData.classifyResult.length }, (_, index) => ({
      value: classifyData.classifyResult[index].value,
      text: classifyData.classifyResult[index].value,
      suffix: <Chip style={{ marginLeft: "1rem" }} text={classifyData.classifyResult[index].count} />,
    }));
  }, [classifyData]);

  const onCancel = useCallback(() => {
    setSpecialFilter({
      active: false,
    });
  }, [setSpecialFilter]);

  const onApply = useCallback(() => {
    if (classifyData === undefined) {
      return;
    }

    updateFiltersByLayer(layerName, {
      [attribute]: {
        value: selected,
        filterType,
        dataType: classifyData.type,
      },
    });

    setSpecialFilter({
      active: false,
    });
  }, [attribute, classifyData, filterType, layerName, selected, setSpecialFilter, updateFiltersByLayer]);

  useEffect(() => {
    const attributeFilter = tableFilters?.[layerName]?.[attribute];

    if (attributeFilter && Array.isArray(attributeFilter.value)) {
      setSelected(attributeFilter.value as string[]);
    }
  }, [attribute, layerName, tableFilters]);

  useEffect(() => {
    refetchStatistics();
    refetchClassify();
  }, [condition]);

  return (
    <>
      <SpecialFilterOverlay bottomHeight={bottomHeight} onClick={onCancel} />
      <SpecialFilterContainer bottomHeight={bottomHeight} ref={ref}>
        {statisticsLoading || classifyLoading ? (
          <LinearProgress />
        ) : (
          <>
            <SpecialFilterHeader>
              <SpecialFilterHeaderInfo>
                <FilledValues count={statCount} totalCount={totalCount} />
                <UniqueValues distinctCount={distinctCount} />
              </SpecialFilterHeaderInfo>
              <SpecialFilterHeaderWorkarea>
                <SpecialFilterHeaderInput>
                  <Dropdown
                    width="100%"
                    menuMaxHeight="10rem"
                    menuMinWidth="15rem"
                    searchable
                    options={options}
                    value={selected}
                    onChange={items => setSelected(items.map(item => item.value))}
                    multiple
                    zIndex={103}
                    placeholder={t("checkboxesPlaceholder")}
                    searchPlaceholder={t("search")}
                  />
                </SpecialFilterHeaderInput>
                <SpecialFilterActions>
                  <IconButton kind={"close"} error onClick={onCancel} data-popover={"close"} />
                  <IconButton kind={"success"} primary onClick={onApply} data-popover={"close"} />
                </SpecialFilterActions>
              </SpecialFilterHeaderWorkarea>
            </SpecialFilterHeader>
            <CheckboxStatisticsChart
              data={data}
              width={chartWidth}
              height={130}
              selected={selected}
              setSelected={setSelected}
            />
          </>
        )}
      </SpecialFilterContainer>
    </>
  );
};
